import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TopStrip from '@/components/TopStrip';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import api from '@/lib/api';
import { isLong } from '@/lib/utils';
import LoadState from "@/components/LoadState";
import { useT } from "@/contexts/LanguageContext";
import { Newspaper, AlertCircle } from 'lucide-react';

export default function NewsPage() {
  const { t, heading } = useT();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.get('/news').then(r => setNews(r.data)).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFCF5]">
      <TopStrip />
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className={`${heading} text-2xl md:text-4xl text-[#621B00] mb-1`} data-testid="news-title">{t("Temple News", "ఆలయ వార్తలు")}</h1>
        </div>
        {loading ? <p className="text-center text-[#8D6E63]">{t('Loading...', 'లోడ్ అవుతోంది...')}</p> : news.length === 0 ? (
          <LoadState error={loadError} emptyText={t('No news or announcements yet.', 'ఇంకా వార్తలు లేదా ప్రకటనలు లేవు.')} />
        ) : (
          <div className="space-y-4">
            {/* Each item is its own route (/news/:id) rather than only living
                inline here, so it gets its own indexable URL, title and meta
                description - see NewsDetail.jsx. */}
            {news.map(n => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className={`block bg-white border rounded-xl p-6 hover:border-[#C43E00]/40 transition-colors ${n.is_important ? 'border-[#C43E00]/30 bg-[#C43E00]/5' : 'border-[#E6DCCA]'}`}
                data-testid={`news-item-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  {n.is_important ? <AlertCircle className="h-5 w-5 text-[#C43E00] shrink-0 mt-0.5" /> : <Newspaper className="h-5 w-5 text-[#8D6E63] shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="font-medium text-[#2D1B0E]">{t(n.title, n.title_telugu)}</h2>
                      {n.is_important && <span className="px-2 py-0.5 bg-[#C43E00] text-white text-xs rounded-full">{t('Important', 'ముఖ్యమైనది')}</span>}
                    </div>
                    {/* Keep the post's own line breaks and show a preview only, as on
                        the Live Blog - the full text is on the page this card links to. */}
                    <p className="text-sm text-[#5D4037] leading-relaxed whitespace-pre-line line-clamp-6">{t(n.content, n.content_telugu)}</p>
                    {isLong(t(n.content, n.content_telugu)) && (
                      <span className="inline-block mt-1 text-sm font-medium text-[#C43E00]">{t('Read more →', 'పూర్తిగా చదవండి →')}</span>
                    )}
                    <p className="text-xs text-[#8D6E63] mt-3">{new Date(n.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
